import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { goEntitiesApi } from "@/services/go-entities.service";
import { adminApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { notifyModuleError, notifyModuleSuccess } from "@/lib/module-toast";
import type { GoImportJob } from "@/types/go-entity";

type ConflictPolicy = "error" | "skip" | "update";

/**
 * Админ-консоль seed/импорта пакетов ГО (только admin.access).
 */
export default function GoAdminImportConsole() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [conflictPolicy, setConflictPolicy] = useState<ConflictPolicy>("skip");
  const [isoFilter, setIsoFilter] = useState("");
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobSnapshot, setJobSnapshot] = useState<GoImportJob | null>(null);
  const [busy, setBusy] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ["admin", "system-settings"],
    queryFn: () => adminApi.getSystemSettings(),
  });
  const goEnabled = settingsQuery.data?.module_go_enabled ?? true;

  const coverageQuery = useQuery({
    queryKey: ["go-import-coverage"],
    queryFn: () => goEntitiesApi.getImportCoverage(),
    enabled: goEnabled,
  });

  const jobQuery = useQuery({
    queryKey: ["go-import-job", jobId],
    queryFn: () => goEntitiesApi.getImportJob(jobId!),
    enabled: jobId != null && goEnabled,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "pending" || s === "queued" || s === "running" ? 2000 : false;
    },
  });

  useEffect(() => {
    if (jobQuery.data) setJobSnapshot(jobQuery.data);
  }, [jobQuery.data]);

  useEffect(() => {
    const s = jobSnapshot?.status;
    if (s === "completed" || s === "failed") {
      void queryClient.invalidateQueries({ queryKey: ["go-import-coverage"] });
    }
  }, [jobSnapshot?.status, queryClient]);

  const parseIsoFilter = (): string[] | undefined => {
    const parts = isoFilter
      .split(/[\s,;]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    return parts.length ? parts : undefined;
  };

  const run = async (label: string, fn: () => Promise<void>) => {
    if (!goEnabled) {
      notifyModuleError(toast, label, new Error("Модуль ГО выключен в настройках системы"));
      return;
    }
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      notifyModuleError(toast, label, e as Error);
    } finally {
      setBusy(false);
    }
  };

  const cov = coverageQuery.data;
  const jobStuckQueued =
    jobSnapshot != null &&
    (jobSnapshot.status === "queued" || jobSnapshot.status === "pending") &&
    !(jobSnapshot.report_json && Object.keys(jobSnapshot.report_json).length > 1);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-foreground">Импорт / seed пакет ГО</h2>
        <SectionHelpTooltip label="Справка: импорт ГО">
          <p className="font-medium">Зачем</p>
          <p>
            Административная загрузка странового пакета (~76 ISO3, вкл. RUS), регионов и seed шаблонов/маппинга.
            Не для повседневной работы оператора.
          </p>
          <p className="mt-2 font-medium">Как работать</p>
          <ul className="list-disc pl-4">
            <li>Сначала dry-run стран — отчёт без записи в БД (в т.ч. число полей ok20).</li>
            <li>Commit пишет геометрию, карточку и materials (флаги/гербы/растры) из пакета.</li>
            <li>
              <strong>skip</strong> — уже существующую страну не трогает (materials не догружает).
            </li>
            <li>
              <strong>update</strong> — обновляет поля, геометрию и materials у существующих.
            </li>
            <li>ISO-фильтр опционален (CUB, AGO, RUS); пусто = весь пакет (~76).</li>
            <li>Celery-воркер должен слушать очередь <code>import</code> (иначе job зависает в queued).</li>
            <li>Регионы — синхронно, без geometry в seed.</li>
          </ul>
          <p className="mt-2 font-medium">Права</p>
          <p>Только администратор (`admin.access`). Обычный `go.edit` недостаточен.</p>
        </SectionHelpTooltip>
      </div>

      {!goEnabled ? (
        <Alert variant="destructive">
          <AlertTitle>Модуль ГО выключен</AlertTitle>
          <AlertDescription>
            Включите «Географический обзор (ГО)» в настройках системы, затем повторите импорт.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">Покрытие seed в БД</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!goEnabled || coverageQuery.isFetching}
            onClick={() => coverageQuery.refetch()}
          >
            Обновить
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {coverageQuery.isLoading ? (
            <p className="text-xs text-muted-foreground">Загрузка сводки…</p>
          ) : coverageQuery.isError ? (
            <p className="text-xs text-destructive">Не удалось загрузить сводку покрытия.</p>
          ) : cov ? (
            <>
              <p className="text-xs text-muted-foreground">{cov.package_scope_hint}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  Страны: <span className="font-medium">{cov.countries_total}</span>
                </div>
                <div>
                  Flag: <span className="font-medium">{cov.with_flag}</span>
                  {cov.missing_flag_count > 0 ? (
                    <span className="text-muted-foreground"> · нет {cov.missing_flag_count}</span>
                  ) : null}
                </div>
                <div>
                  Coat: <span className="font-medium">{cov.with_coat}</span>
                  {cov.missing_coat_count > 0 ? (
                    <span className="text-muted-foreground"> · нет {cov.missing_coat_count}</span>
                  ) : null}
                </div>
                <div>
                  Symbols: <span className="font-medium">{cov.with_symbols}</span>
                </div>
                <div>
                  Raster (страны): <span className="font-medium">{cov.with_raster}</span>
                </div>
                <div>
                  Файлов materials: <span className="font-medium">{cov.materials_total}</span>
                </div>
              </div>
              {cov.missing_flag_iso3.length > 0 ? (
                <p className="text-xs text-muted-foreground break-all">
                  Без флага (sample): {cov.missing_flag_iso3.slice(0, 20).join(", ")}
                  {cov.missing_flag_count > 20 ? "…" : ""}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">У всех стран в БД есть flag.</p>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Страны (Celery)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="go-conflict">Политика конфликта</Label>
              <Select
                value={conflictPolicy}
                onValueChange={(v) => setConflictPolicy(v as ConflictPolicy)}
                disabled={!goEnabled || busy}
              >
                <SelectTrigger id="go-conflict" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="skip">skip</SelectItem>
                  <SelectItem value="update">update</SelectItem>
                  <SelectItem value="error">error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 min-w-[200px] flex-1">
              <Label htmlFor="go-iso">ISO-фильтр (опц.)</Label>
              <Input
                id="go-iso"
                placeholder="AGO, BRA …"
                value={isoFilter}
                onChange={(e) => setIsoFilter(e.target.value)}
                disabled={!goEnabled || busy}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!goEnabled || busy}
              onClick={() =>
                run("dry-run импорта стран", async () => {
                  const job = await goEntitiesApi.startCountryImport({
                    dry_run: true,
                    conflict_policy: conflictPolicy,
                    iso_filter: parseIsoFilter(),
                  });
                  setJobId(job.id);
                  setJobSnapshot(job);
                  notifyModuleSuccess(toast, `Dry-run в очереди (#${job.id})`);
                })
              }
            >
              Dry-run
            </Button>
            <Button
              type="button"
              disabled={!goEnabled || busy}
              onClick={() =>
                run("commit импорта стран", async () => {
                  const job = await goEntitiesApi.startCountryImport({
                    dry_run: false,
                    conflict_policy: conflictPolicy,
                    iso_filter: parseIsoFilter(),
                  });
                  setJobId(job.id);
                  setJobSnapshot(job);
                  notifyModuleSuccess(toast, `Импорт в очереди (#${job.id})`);
                })
              }
            >
              Commit импорт
            </Button>
            {jobId != null ? (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => jobQuery.refetch()}
              >
                Обновить статус
              </Button>
            ) : null}
          </div>
          {jobStuckQueued ? (
            <Alert>
              <AlertTitle>Job в очереди</AlertTitle>
              <AlertDescription className="text-xs">
                Если статус долго остаётся <code>queued</code>/<code>pending</code>, проверьте, что
                Celery worker слушает очередь <code>import</code> (dev:{" "}
                <code>--queues=default,import,export,sync</code>).
              </AlertDescription>
            </Alert>
          ) : null}
          {jobSnapshot ? (
            <div className="rounded border border-border bg-muted/30 p-3 text-xs space-y-1">
              <div>
                Job #{jobSnapshot.id}: <span className="font-medium">{jobSnapshot.status}</span>
                {jobSnapshot.dry_run ? " · dry-run" : ""} · {jobSnapshot.conflict_policy}
              </div>
              {jobSnapshot.error_message ? (
                <div className="text-destructive">{jobSnapshot.error_message}</div>
              ) : null}
              {jobSnapshot.report_json && Object.keys(jobSnapshot.report_json).length > 0 ? (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all text-[11px] text-muted-foreground">
                  {JSON.stringify(jobSnapshot.report_json, null, 2)}
                </pre>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Статус job появится после запуска.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Регионы (main.json)</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!goEnabled || busy}
            onClick={() =>
              run("dry-run регионов", async () => {
                const r = await goEntitiesApi.dryRunRegionImport();
                notifyModuleSuccess(
                  toast,
                  `Dry-run: ${r.regions} рег. / ${r.districts} р-нов${r.geometry_note ? ` · ${r.geometry_note}` : ""}`,
                );
              })
            }
          >
            Dry-run регионов
          </Button>
          <Button
            type="button"
            disabled={!goEnabled || busy}
            onClick={() =>
              run("импорт регионов", async () => {
                const r = await goEntitiesApi.importRegions();
                notifyModuleSuccess(toast, `Импорт: ${r.regions} рег. / ${r.districts} р-нов`);
              })
            }
          >
            Import регионов
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Seed шаблонов и ISO↔OKSM</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!goEnabled || busy}
            onClick={() =>
              run("seed ok20 core", async () => {
                const t = await goEntitiesApi.seedOk20Template();
                notifyModuleSuccess(toast, `ok20 core #${t.template_id} (${t.status})`);
              })
            }
          >
            Seed ok20 core
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!goEnabled || busy}
            onClick={() =>
              run("seed ok20 full", async () => {
                const full = await goEntitiesApi.seedOk20Full();
                notifyModuleSuccess(
                  toast,
                  `ok20 full #${full.template_id} · полей ${full.fields_parsed ?? full.fields_added ?? "?"}`,
                );
              })
            }
          >
            Seed ok20 full
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!goEnabled || busy}
            onClick={() =>
              run("seed ISO↔OKSM", async () => {
                const m = await goEntitiesApi.seedIsoOksm();
                notifyModuleSuccess(toast, `ISO↔OKSM: ${m.upserted}/${m.isos_found}`);
              })
            }
          >
            Seed ISO↔OKSM
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
